import { formatQuantity } from '../../utils/ingredientParser';

/** Units that mean "a countable thing" rather than a measured amount. */
const COUNT_UNITS = new Set(['piece', 'pieces', 'pc', 'pcs', 'unit', 'units', 'count', '']);

/**
 * Short quantity label for a scanned item, or null when there is nothing worth
 * showing.
 *
 * A receipt line is one unit of something unless it says otherwise, so "1
 * piece" is the default rather than information — printing it on every row
 * would bury the handful of rows that genuinely carry an amount (1.08 kg of
 * salmon, a 10 lb bag of onions, two tubs of salad).
 *
 *   (1.08, 'kg')    → "1.08 kg"
 *   (10, 'lb')      → "10 lb"
 *   (2, 'piece')    → "×2"
 *   (1, 'piece')    → null
 *   (undefined, …)  → null
 */
export const captureQuantityLabel = (
  quantity: number | undefined,
  unit: string | undefined,
): string | null => {
  if (typeof quantity !== 'number' || !Number.isFinite(quantity) || quantity <= 0) return null;

  const normalizedUnit = (unit || '').trim().toLowerCase();
  const isCount = COUNT_UNITS.has(normalizedUnit);

  if (isCount) {
    return quantity > 1 ? `×${formatQuantity(quantity)}` : null;
  }
  return `${formatQuantity(quantity, normalizedUnit)} ${normalizedUnit}`;
};
